/** Sub-PR E2 §5.2 · 功能磚彈出面板。
 *
 * 資料源：GET /api/system/functions（依角色權限過濾·與 TopNav/Sidebar 同）+ GET /scopes（可對話功能）。
 * 過濾 func_order < 10（隱藏功能不進磚）。E4：不再有不限範圍磚（聊天模式首頁即一般對話）。
 * 磚分兩種（依 /scopes）：可對話 → 進專案頁；不可對話 → 白名單 modal 開啟·否則跳頁（角落 ↗）。
 * §5（D14）：排除 personal_function 節點底下的功能（個人化功能只在個人選單·以節點判斷不寫死清單）。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, InputAdornment, Popover, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NorthEastIcon from '@mui/icons-material/NorthEast';

import { getI18nValue } from '../../../../utils/i18nHelper';
import { FuncType } from '../../../../utils/funcType';
import type { SystemFunction } from '../../../../types';

export type TileAction =
  | { kind: 'scope'; funcCode: string; funcName: SystemFunction['func_name'] }
  | { kind: 'modal'; funcCode: string; funcName: SystemFunction['func_name'] }
  | { kind: 'navigate'; funcCode: string };

/** 功能的開啟方式判斷（lab R1·功能磚與個人選單共用）：
 *  有聊天工具（在 scopes）→ 進專屬對話；在視窗白名單（embeddable）→ 開視窗 modal；都不是 → 跳頁。
 *  順序固定：scope 優先於 modal 優先於 navigate。 */
export const decideTileAction = (
  func: Pick<SystemFunction, 'func_code' | 'func_name'>,
  scopes: Set<string>,
  embeddable: (funcCode: string) => boolean,
): TileAction => {
  if (scopes.has(func.func_code)) return { kind: 'scope', funcCode: func.func_code, funcName: func.func_name };
  if (embeddable(func.func_code)) return { kind: 'modal', funcCode: func.func_code, funcName: func.func_name };
  return { kind: 'navigate', funcCode: func.func_code };
};

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  functions: SystemFunction[];
  scopes: Set<string>;              // 可對話 func_code
  embeddable: (funcCode: string) => boolean;
  onClose: () => void;
  onAction: (a: TileAction) => void;
}

const FunctionTilesPopover: React.FC<Props> = ({
  open, anchorEl, functions, scopes, embeddable, onClose, onAction,
}) => {
  const { t, i18n } = useTranslation();
  const [q, setQ] = React.useState('');

  // 只取可顯示於選單的 PAGE 葉（func_order >= 10），依其頂層祖先分組
  const byId = React.useMemo(() => {
    const m = new Map<number, SystemFunction>();
    functions.forEach((f) => m.set(f.id, f));
    return m;
  }, [functions]);

  const topAncestor = React.useCallback((f: SystemFunction): SystemFunction => {
    let cur = f;
    const seen = new Set<number>();
    while (cur.upper_func_id && byId.has(cur.upper_func_id) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.upper_func_id)!;
    }
    return cur;
  }, [byId]);

  const groups = React.useMemo(() => {
    const kw = q.trim().toLowerCase();
    const leaves = functions.filter((f) =>
      f.func_type === FuncType.PAGE && f.func_order >= 10 &&
      (!kw || getI18nValue(f.func_name, i18n.language).toLowerCase().includes(kw)));
    const g = new Map<string, { title: string; icon?: string; items: SystemFunction[] }>();
    leaves.forEach((f) => {
      const top = topAncestor(f);
      if (top.func_code === 'personal_function') return;   // §5/D14：個人化功能不進功能磚
      const key = String(top.id);
      if (!g.has(key)) {
        g.set(key, { title: getI18nValue(top.func_name, i18n.language), icon: top.func_icon || undefined, items: [] });
      }
      g.get(key)!.items.push(f);
    });
    return Array.from(g.values()).filter((x) => x.items.length > 0);
  }, [functions, q, i18n.language, topAncestor]);

  const tileFor = (f: SystemFunction): TileAction => decideTileAction(f, scopes, embeddable);

  const renderTile = (label: string, icon: React.ReactNode, jumps: boolean, onClick: () => void, key: string) => (
    <Box
      key={key}
      onClick={() => { onClick(); onClose(); }}
      role="button"
      tabIndex={0}
      sx={{
        position: 'relative', cursor: 'pointer', width: 96, minHeight: 84,
        borderRadius: 2, border: '1px solid', borderColor: 'divider',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 0.5, p: 1, textAlign: 'center',
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
      }}
    >
      {jumps && (
        <NorthEastIcon sx={{ position: 'absolute', top: 4, right: 4, fontSize: 14, color: 'text.disabled' }} />
      )}
      <Box sx={{ fontSize: 24, lineHeight: 1 }}>{icon}</Box>
      <Typography variant="caption" sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>{label}</Typography>
    </Box>
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 'min(380px, calc(100vw - 32px))', maxHeight: 520, p: 2 } } }}
    >
      <TextField
        fullWidth size="small" autoFocus placeholder={t('chat.tiles.search', '搜尋功能…')}
        value={q} onChange={(e) => setQ(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        sx={{ mb: 1.5 }}
      />
      {/* E4 §4.3：移除不限範圍磚（聊天模式首頁即一般對話·由頂部列模式下拉進入） */}
      {groups.map((grp, gi) => (
        <Box key={gi} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            {grp.icon ? `${grp.icon} ` : ''}{grp.title}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {grp.items.map((f) => {
              const action = tileFor(f);
              const jumps = action.kind === 'navigate';
              return renderTile(
                getI18nValue(f.func_name, i18n.language),
                <span>{f.func_icon || '▧'}</span>,
                jumps,
                () => onAction(action),
                f.func_code,
              );
            })}
          </Box>
        </Box>
      ))}
      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {t('chat.tiles.empty', '沒有符合的功能')}
        </Typography>
      )}
    </Popover>
  );
};

export default FunctionTilesPopover;
